/**
 * Popstate listener corral - patches addEventListener to guard popstate handlers.
 *
 * Original bug (1f5d094a): vue-router 4's `createWebHistory(base)` registers a
 * `window.popstate` listener at construction and never removes it on
 * `app.unmount()`. After single-spa unmounts a parcel, the parcel's Vue app is
 * torn down but the router's popstate listener stays alive. On the next
 * navigation each zombie listener runs vue-router's `changeLocation()`, which
 * strips its base from `location.pathname`; when the strip fails it falls back
 * to the verbatim pathname and writes `replaceState(base + verbatim)`. Multiple
 * zombies compound - `/central/units/central/onboarding/central/units/...`.
 *
 * v2 fix has two parts. Both are required.
 *
 * 1. Active-app guard - wrap each popstate handler so it only runs while its
 *    owning app is mounted. Zombies are silenced.
 *
 * 2. Targeted catch-up - store each app's original handlers in a per-app
 *    registry. On activation, replay a synthetic popstate **directly** to that
 *    app's handlers so its vue-router resyncs to the current URL. This closes
 *    the gap that broke `navigateTo` after the v1 corral landed: silenced
 *    handlers missed the popstate that single-spa fires from its patched
 *    `pushState`, leaving vue-router's `currentRoute` stale on remount.
 *
 * Direct invocation (not `window.dispatchEvent`) is intentional:
 * `dispatchEvent` would re-trigger single-spa's urlReroute and fire every
 * other active app's handler - the cascade we silence zombies to prevent.
 *
 * `installPopstateCorral()` MUST be called AFTER `single-spa.start()` so the
 * corral wraps single-spa's addEventListener patch as the outermost layer.
 */

let originalAddEventListener: typeof window.addEventListener;
let installed = false;

// Concurrent app loads are serialised by `withPopstateGuard`'s loadChain, so a
// single global is safe. Reference-equality alone wouldn't catch double-install
// if a third party wrapped addEventListener after us - `installed` does.
let currentLoadingAppId: string | null = null;
const activeApps = new Set<string>();

// Append-only by contract. Same-appId reloads do not occur:
// `registerNewRemoteApp` dedupes via single-spa.getAppNames(), and ADR-003
// forbids unregister+re-register. Clearing the registry on a hypothetical
// second guard would leave syncApp() with nothing to replay -> vue-router's
// currentRoute stays stale on remount (v1 regression).
const appListeners = new Map<string, EventListener[]>();

// ── State management (consumed by popstate-lifecycle.ts) ─────────────────────

export function setCurrentApp(appId: string): void {
  currentLoadingAppId = appId;
}

export function clearCurrentApp(): void {
  currentLoadingAppId = null;
}

export function activateApp(appId: string): void {
  activeApps.add(appId);
}

export function deactivateApp(appId: string): void {
  activeApps.delete(appId);
}

/**
 * Replay a synthetic popstate directly to the named app's handlers so its
 * vue-router resyncs to the current URL. Only the named app fires - no global
 * dispatch, no single-spa reroute, no other apps invoked.
 */
export function syncApp(appId: string): void {
  const handlers = appListeners.get(appId);
  if (!handlers || handlers.length === 0) return;

  const event = new PopStateEvent('popstate', { state: window.history.state });
  for (const handler of handlers) {
    try {
      // Invoke with `window` as `this` to match how a real DOM-dispatched
      // popstate would call a window-registered listener. vue-router 4 reads
      // only `event.state`, but custom handlers may rely on the binding.
      handler.call(window, event);
    } catch {
      // A handler that throws must not block the rest of the catch-up or the
      // mount lifecycle. Mirrors single-spa's own try/catch around captured
      // event listeners (callCapturedEventListeners in single-spa core).
    }
  }
}

// ── addEventListener patch ───────────────────────────────────────────────────

function patchedAddEventListener(
  type: string,
  handler: EventListenerOrEventListenerObject | null,
  options?: boolean | AddEventListenerOptions,
): void {
  if (type === 'popstate' && typeof handler === 'function' && currentLoadingAppId) {
    const appId = currentLoadingAppId;
    const original = handler;
    // removeEventListener with the original ref is a no-op (wrapper ref
    // doesn't match) - intentional. activeApps silences live events; the
    // registry keeps the original for syncApp() catch-up. Pruning on
    // removeEventListener would yank vue-router's handler before catch-up
    // (v1 regression).
    const guarded: EventListener = (event: Event) => {
      if (activeApps.has(appId)) original(event);
    };

    const handlers = appListeners.get(appId);
    if (handlers) handlers.push(original);
    else appListeners.set(appId, [original]);

    originalAddEventListener(type, guarded, options);
    return;
  }

  originalAddEventListener(type, handler as EventListener, options);
}

export function installPopstateCorral(): void {
  // A module-level flag - not reference equality on `window.addEventListener`
  // - so a third-party wrapper installed after us cannot trick a second
  // install into rebinding `originalAddEventListener` to that wrapper, which
  // would create infinite recursion when the wrapper delegated back to the
  // patched function.
  if (installed) return;

  originalAddEventListener = window.addEventListener.bind(window);
  (window as unknown as Record<string, unknown>).addEventListener = patchedAddEventListener;
  installed = true;
}

// ── Test helpers ─────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/naming-convention
export function __resetPopstateCorralForTesting(): void {
  if (originalAddEventListener) {
    (window as unknown as Record<string, unknown>).addEventListener = originalAddEventListener;
  }
  activeApps.clear();
  appListeners.clear();
  currentLoadingAppId = null;
  installed = false;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function __getActiveAppsForTesting(): ReadonlySet<string> {
  return activeApps;
}
